import { useCallback, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Dialog,
  DialogBody,
  DialogFooter,
  Text,
  Textarea,
} from '@bitrise/bitkit';
import useBuildCacheDeactivateFeedback from './hooks/useBuildCacheDeactivateFeedback';

interface DeactivateBuildCacheDialogProps {
  isOpen: boolean;
  workspaceSlug: string;
  onActivateSelected: () => void;
  onReasonSubmitted: () => void;
  onClose: () => void;
}

const DeactivateBuildCacheDialog = ({
  isOpen,
  workspaceSlug,
  onActivateSelected,
  onReasonSubmitted,
  onClose,
}: DeactivateBuildCacheDialogProps): JSX.Element => {
  const reasons = useMemo<{ value: string; text: string }[]>(() => {
    return [
      {
        value: 'duration_did_not_improve_enough',
        text: 'Duration did not improve enough',
      },
      {
        value: 'trouble_optimising',
        text: 'Trouble optimising',
      },
      {
        value: 'value_for_money',
        text: 'Value for money',
      },
      {
        value: 'unclear_pricing',
        text: 'Unclear pricing',
      },
    ];
  }, []);
  const [selectedReasonValues, setSelectedReasonValues] = useState<string[]>([]);

  const onReasonsChange = useCallback((values: string[]) => {
    setSelectedReasonValues(values);
  }, []);

  const [otherReason, setOtherReason] = useState('');

  const { submit: onSubmitFeedbackSelected, isPending: isSubmittingFeedback } = useBuildCacheDeactivateFeedback(
    workspaceSlug,
    selectedReasonValues,
    otherReason,
    onReasonSubmitted,
    onReasonSubmitted,
  );

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Remote build cache deactivated">
      <>
        <DialogBody>
          <Box display="flex" flexDirection="column" gap="2rem">
            <Box display="flex" flexDirection="column" gap=".25rem">
              <Text>Thank you for trialing the feature.</Text>
              <Text>Your feedback can help us improve the remote build cache experience.</Text>
            </Box>
            <Box display="flex" flexDirection="column" gap=".75rem">
              <Box display="flex" flexDirection="column" gap=".5rem">
                <Text size="2" fontWeight="bold">
                  What are the reasons to deactivate remote build caching?{' '}
                </Text>
                <Text size="2" textColor="neutral.50">
                  Select all relevant options
                </Text>
              </Box>
              <CheckboxGroup
                defaultValue={[]}
                display="flex"
                flexDirection="column"
                gap=".75rem"
                onChange={onReasonsChange}
              >
                {reasons.map(({ value, text }) => (
                  <Checkbox value={value} key={value}>
                    {text}
                  </Checkbox>
                ))}
              </CheckboxGroup>
            </Box>
            <Box>
              <Textarea
                onChange={(e) => setOtherReason(e.target.value)}
                label="Other"
                placeholder="reason to cancel subscription"
                isLoading={isSubmittingFeedback}
              />
            </Box>
          </Box>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onActivateSelected}>
            Activate remote build cache
          </Button>
          <Button
            variant="primary"
            onClick={onSubmitFeedbackSelected}
            isLoading={isSubmittingFeedback}
            isDisabled={selectedReasonValues.length === 0 && !otherReason}
          >
            Submit
          </Button>
        </DialogFooter>
      </>
    </Dialog>
  );
};

export default DeactivateBuildCacheDialog;
